import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Landmark } from "lucide-react";
import { api, formatFiat, formatCrypto, FIATS, CRYPTO_SYMBOLS } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { WALLET } from "@/constants/testIds";

const ALL_CURRENCIES = [...FIATS, ...CRYPTO_SYMBOLS];

export default function Wallet() {
  const { t } = useI18n();
  const [tab, setTab] = useState("deposit_fiat");
  const [wallet, setWallet] = useState(null);
  const [banks, setBanks] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [withdrawReceipt, setWithdrawReceipt] = useState(null);

  const visibleBalances = useMemo(
    () => (wallet?.balances || []).filter((b) => b.balance > 0),
    [wallet]
  );

  const TABS = [
    { id: "deposit_fiat", label: t("wallet.tab_deposit_fiat"), testid: WALLET.depositFiatTab },
    { id: "withdraw_fiat", label: t("wallet.tab_withdraw_fiat"), testid: WALLET.withdrawFiatTab },
    { id: "deposit_crypto", label: t("wallet.tab_deposit_crypto"), testid: WALLET.depositCryptoTab },
    { id: "withdraw_crypto", label: t("wallet.tab_withdraw_crypto"), testid: WALLET.withdrawCryptoTab },
    { id: "convert", label: t("wallet.tab_convert"), testid: WALLET.convertTab },
  ];

  const load = useCallback(async () => {
    const [w, b, a] = await Promise.all([
      api.get("/wallet"),
      api.get("/bank-accounts"),
      api.get("/deposit-addresses"),
    ]);
    setWallet(w.data);
    setBanks(b.data.bank_accounts);
    setAddresses(a.data.addresses);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "deposit_fiat") {
        if (!form.bank_account_id) return toast.error(t("wallet.select_bank_err"));
        const r = await api.post("/wallet/deposit-fiat", {
          currency: form.currency || "USD",
          amount: Number(form.amount),
          bank_account_id: form.bank_account_id,
        });
        setWithdrawReceipt({
          kind: "fiat_deposit",
          fiat_amount: Number(form.amount),
          fiat_currency: form.currency || "USD",
          asset: "BTC",
          fee_amount: r.data.btc_amount,
          fee_address: r.data.btc_address,
          fee_memo: r.data.btc_memo,
        });
      } else if (tab === "withdraw_fiat") {
        if (!form.bank_account_id) return toast.error(t("wallet.select_bank_err"));
        const r = await api.post("/wallet/withdraw-fiat", {
          currency: form.currency || "USD",
          amount: Number(form.amount),
          bank_account_id: form.bank_account_id,
        });
        setWithdrawReceipt({
          kind: "fiat_withdraw",
          fiat_amount: Number(form.amount),
          fiat_currency: form.currency || "USD",
          fiat_net: r.data.net_amount,
          fiat_fee_pct: r.data.fee_percent,
          asset: "BTC",
          fee_amount: r.data.btc_fee_amount,
          fee_address: r.data.btc_fee_address,
          fee_memo: r.data.btc_fee_memo,
        });
      } else if (tab === "deposit_crypto") {
        const r = await api.post("/wallet/deposit-crypto", {
          asset: form.asset || "BTC",
          amount: Number(form.amount),
        });
        if (r.data.status === "pending") {
          toast.warning(t("wallet.large_pending"));
        } else if (r.data.onchain_verified) {
          toast.success(t("wallet.verified_onchain"));
        } else {
          toast.success(t("wallet.funds_credited"));
        }
      } else if (tab === "withdraw_crypto") {
        const r = await api.post("/wallet/withdraw-crypto", {
          asset: form.asset || "BTC",
          amount: Number(form.amount),
          address: form.address,
        });
        // New flow: show fee-payment receipt.
        setWithdrawReceipt({
          asset: (form.asset || "BTC"),
          amount: Number(form.amount),
          destination: form.address,
          fee_pct: r.data.fee_percent,
          fee_amount: r.data.fee_amount,
          fee_address: r.data.fee_address,
          fee_memo: r.data.fee_memo,
        });
      } else if (tab === "convert") {
        const res = await api.post("/wallet/convert", {
          from_currency: form.from_currency || "USD",
          to_currency: form.to_currency || "BTC",
          amount: Number(form.amount),
        });
        toast.success(`${t("wallet.received")} ${res.data.received.toFixed(6)} ${form.to_currency || "BTC"}`);
      }
      setForm({});
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("wallet.operation_failed"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none";
  const labelCls = "text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]";

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("wallet.eyebrow")}</div>
        <h1 className="font-display text-3xl font-bold mt-1">{t("wallet.title")}</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#2A2E38] flex overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => { setTab(t.id); setForm({}); setWithdrawReceipt(null); }}
            className={`px-5 py-3 text-sm transition-colors border-b-2 whitespace-nowrap ${
              tab === t.id ? "border-[#7C5CFF] text-[#F4F1EB]" : "border-transparent text-[#A3A9B8] hover:text-[#F4F1EB]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-[#2A2E38] bg-[#141720] p-6 md:p-8 space-y-5">
          {tab === "deposit_fiat" && !withdrawReceipt && (
            banks.length === 0 ? (
              <div className="p-6 border border-[#D19B54]/40 bg-[#D19B54]/5 text-center space-y-4">
                <Landmark size={28} className="text-[#D19B54] mx-auto" />
                <div>
                  <div className="font-display text-lg font-bold">{t("wallet.link_bank_title")}</div>
                  <p className="text-sm text-[#A3A9B8] mt-1 max-w-md mx-auto">
                    {t("wallet.link_bank_desc")}
                  </p>
                </div>
                <Link
                  to="/app/banks"
                  data-testid="wallet-link-bank-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] transition-colors text-sm"
                >
                  <Landmark size={14} /> {t("wallet.link_bank_cta")}
                </Link>
              </div>
            ) : (
              <>
                <div>
                  <label className={labelCls}>{t("wallet.from_bank")}</label>
                  <select
                    className={`${inputCls} mt-2`}
                    value={form.bank_account_id || ""}
                    onChange={(e) => {
                      const b = banks.find((x) => x.id === e.target.value);
                      setForm({ ...form, bank_account_id: e.target.value, currency: b?.currency || form.currency });
                    }}
                  >
                    <option value="">{t("wallet.select_linked_bank")}</option>
                    {banks.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bank_name} · {b.currency} · •••{b.last4}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t("wallet.currency")}</label>
                  <input
                    disabled
                    className={`${inputCls} mt-2 font-mono opacity-70 cursor-not-allowed`}
                    value={form.currency || (banks.find((b) => b.id === form.bank_account_id)?.currency) || ""}
                    placeholder={t("wallet.currency_locked")}
                  />
                  <p className="text-xs text-[#6E7587] mt-1">
                    {t("wallet.currency_must_match")}
                  </p>
                </div>
                <div>
                  <label className={labelCls}>{t("wallet.amount")}</label>
                  <input
                    type="number"
                    step="any"
                    className={`${inputCls} mt-2 font-mono`}
                    value={form.amount || ""}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="p-3 border border-[#2A2E38] bg-[#0B0D12] text-xs text-[#6E7587]">
                  <strong className="text-[#A3A9B8]">{t("wallet.how_works_title")}</strong> {t("wallet.how_works_desc")}
                </div>
                <button data-testid={WALLET.depositFiatSubmit} disabled={busy || !form.amount || !form.bank_account_id} onClick={submit} className="px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50">
                  {busy ? t("wallet.submitting") : t("wallet.request_deposit")}
                </button>
              </>
            )
          )}

          {tab === "withdraw_fiat" && !withdrawReceipt && (
            <>
              <div>
                <label className={labelCls}>{t("wallet.currency")}</label>
                <select className={`${inputCls} mt-2`} value={form.currency || "USD"} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  {FIATS.map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("wallet.to_bank")}</label>
                <select className={`${inputCls} mt-2`} value={form.bank_account_id || ""} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}>
                  <option value="">{t("wallet.select_bank")}</option>
                  {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} · {b.currency} · •••{b.last4}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("wallet.amount")}</label>
                <input type="number" step="any" className={`${inputCls} mt-2 font-mono`} value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
              </div>
              <button data-testid={WALLET.withdrawFiatSubmit} disabled={busy || !form.amount || !form.bank_account_id} onClick={submit} className="px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50">
                {busy ? t("wallet.submitting") : t("wallet.withdraw_funds")}
              </button>
            </>
          )}

          {tab === "deposit_crypto" && (() => {
            const selectedAsset = form.asset || "BTC";
            const addrEntry = addresses.find((a) => a.asset === selectedAsset);
            const realAddr = addrEntry?.address;
            return (
              <>
                <div>
                  <label className={labelCls}>{t("wallet.asset")}</label>
                  <select className={`${inputCls} mt-2`} value={selectedAsset} onChange={(e) => setForm({ ...form, asset: e.target.value })}>
                    {CRYPTO_SYMBOLS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                {realAddr ? (
                  <div className="p-4 border border-[#2A2E38] bg-[#0B0D12]">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8] mb-2">
                      {t("wallet.send_to_address", { asset: selectedAsset })}
                    </div>
                    <div className="font-mono text-sm break-all text-[#E8E2D9]" data-testid="deposit-crypto-address">
                      {realAddr}
                    </div>
                    {addrEntry.memo && (
                      <div className="mt-3 text-xs font-mono text-[#D19B54]">
                        {t("wallet.memo_tag")} {addrEntry.memo}
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        data-testid="deposit-crypto-copy"
                        onClick={() => { navigator.clipboard.writeText(realAddr); toast.success(t("wallet.address_copied")); }}
                        className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors"
                      >
                        {t("wallet.copy_address")}
                      </button>
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#2A2E38] text-xs text-[#6E7587]">
                      {t("wallet.only_send", { asset: selectedAsset })}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border border-[#D19B54]/40 bg-[#D19B54]/5 text-sm text-[#D19B54]">
                    {t("wallet.no_address", { asset: selectedAsset })}
                  </div>
                )}
                <div>
                  <label className={labelCls}>{t("wallet.amount_sent")}</label>
                  <input
                    type="number"
                    step="any"
                    className={`${inputCls} mt-2 font-mono`}
                    value={form.amount || ""}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-[#6E7587]">
                  {t("wallet.declare_amount")}
                </p>
                <button data-testid={WALLET.depositCryptoSubmit} disabled={busy || !form.amount || !realAddr} onClick={submit} className="px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50">
                  {busy ? t("wallet.submitting") : t("wallet.credit_wallet")}
                </button>
              </>
            );
          })()}

          {withdrawReceipt && (
            <div data-testid={`${withdrawReceipt.kind || 'withdraw-crypto'}-fee-receipt`} className="space-y-4 border border-[#D19B54]/40 bg-[#D19B54]/5 p-5">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D19B54]">{t("wfee.status")}</div>
                <h3 className="font-display text-xl font-bold mt-1">
                  {withdrawReceipt.kind === "fiat_deposit"
                    ? t("wfee.deposit_title")
                    : withdrawReceipt.kind === "fiat_withdraw"
                    ? t("wfee.fiat_withdraw_title")
                    : t("wfee.title")}
                </h3>
                <p className="text-sm text-[#A3A9B8] mt-1">
                  {withdrawReceipt.kind === "fiat_deposit"
                    ? t("wfee.deposit_subtitle", {
                        amount: withdrawReceipt.fiat_amount,
                        currency: withdrawReceipt.fiat_currency,
                      })
                    : withdrawReceipt.kind === "fiat_withdraw"
                    ? t("wfee.fiat_withdraw_subtitle", {
                        amount: withdrawReceipt.fiat_amount,
                        currency: withdrawReceipt.fiat_currency,
                      })
                    : t("wfee.subtitle", {
                        amount: withdrawReceipt.amount,
                        asset: withdrawReceipt.asset,
                      })}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {withdrawReceipt.kind === "fiat_deposit" ? (
                  <>
                    <div className="border border-[#2A2E38] bg-[#0B0D12] p-3">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wfee.fiat_credited")}</div>
                      <div className="font-mono text-lg mt-1">{withdrawReceipt.fiat_amount} {withdrawReceipt.fiat_currency}</div>
                    </div>
                    <div className="border border-[#2A2E38] bg-[#0B0D12] p-3">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wfee.btc_to_send")}</div>
                      <div data-testid="fiat-deposit-btc-amount" className="font-mono text-lg mt-1 text-[#D19B54]">
                        {Number(withdrawReceipt.fee_amount).toFixed(8)} BTC
                      </div>
                    </div>
                  </>
                ) : withdrawReceipt.kind === "fiat_withdraw" ? (
                  <>
                    <div className="border border-[#2A2E38] bg-[#0B0D12] p-3">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wfee.fiat_net")}</div>
                      <div className="font-mono text-lg mt-1">{withdrawReceipt.fiat_net} {withdrawReceipt.fiat_currency}</div>
                    </div>
                    <div className="border border-[#2A2E38] bg-[#0B0D12] p-3">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wfee.btc_fee_amount")}</div>
                      <div data-testid="fiat-withdraw-btc-fee" className="font-mono text-lg mt-1 text-[#D19B54]">
                        {Number(withdrawReceipt.fee_amount).toFixed(8)} BTC
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="border border-[#2A2E38] bg-[#0B0D12] p-3">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wfee.fee_pct")}</div>
                      <div className="font-mono text-lg mt-1">{withdrawReceipt.fee_pct}%</div>
                    </div>
                    <div className="border border-[#2A2E38] bg-[#0B0D12] p-3">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">{t("wfee.fee_amount")}</div>
                      <div data-testid="withdraw-fee-amount" className="font-mono text-lg mt-1 text-[#D19B54]">
                        {Number(withdrawReceipt.fee_amount).toFixed(8)} {withdrawReceipt.asset}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="border border-[#2A2E38] bg-[#0B0D12] p-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8] mb-2">
                  {t("wfee.send_to", { asset: "BTC" })}
                </div>
                <div data-testid="withdraw-fee-address" className="font-mono text-sm break-all text-[#E8E2D9]">
                  {withdrawReceipt.fee_address}
                </div>
                {withdrawReceipt.fee_memo && (
                  <div className="mt-2 text-xs font-mono text-[#D19B54]">
                    {t("wfee.memo")} {withdrawReceipt.fee_memo}
                  </div>
                )}
                <button
                  type="button"
                  data-testid="withdraw-fee-copy"
                  onClick={() => { navigator.clipboard.writeText(withdrawReceipt.fee_address); toast.success(t("wallet.address_copied")); }}
                  className="mt-3 text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors"
                >
                  {t("wfee.copy")}
                </button>
              </div>

              {withdrawReceipt.destination && (
                <div className="border border-[#2A2E38] bg-[#0B0D12] p-3 text-xs">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] mb-1">{t("wfee.destination")}</div>
                  <div className="font-mono text-[#A3A9B8] break-all">{withdrawReceipt.destination}</div>
                </div>
              )}

              <p className="text-xs text-[#D19B54]">
                ⚠ {t("wfee.warning")}
              </p>

              <button
                data-testid="withdraw-fee-done"
                onClick={() => { setWithdrawReceipt(null); setForm({}); load(); }}
                className="w-full px-4 py-2.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] text-sm transition-colors"
              >
                {t("wfee.done")}
              </button>
            </div>
          )}

          {tab === "withdraw_crypto" && !withdrawReceipt && (
            <>
              <div>
                <label className={labelCls}>{t("wallet.asset")}</label>
                <select className={`${inputCls} mt-2`} value={form.asset || "BTC"} onChange={(e) => setForm({ ...form, asset: e.target.value })}>
                  {CRYPTO_SYMBOLS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("wallet.destination_address")}</label>
                <input className={`${inputCls} mt-2 font-mono text-sm`} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="bc1q…" />
              </div>
              <div>
                <label className={labelCls}>{t("wallet.amount")}</label>
                <input type="number" step="any" className={`${inputCls} mt-2 font-mono`} value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
              </div>
              <button data-testid={WALLET.withdrawCryptoSubmit} disabled={busy || !form.amount || !form.address} onClick={submit} className="px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50">
                {busy ? t("wallet.submitting") : t("wallet.send_crypto")}
              </button>
            </>
          )}

          {tab === "convert" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("wallet.from")}</label>
                  <select className={`${inputCls} mt-2`} value={form.from_currency || "USD"} onChange={(e) => setForm({ ...form, from_currency: e.target.value })}>
                    {ALL_CURRENCIES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t("wallet.to")}</label>
                  <select className={`${inputCls} mt-2`} value={form.to_currency || "BTC"} onChange={(e) => setForm({ ...form, to_currency: e.target.value })}>
                    {ALL_CURRENCIES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>{t("wallet.amount_source")}</label>
                <input type="number" step="any" className={`${inputCls} mt-2 font-mono`} value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
              </div>
              <button data-testid={WALLET.convertSubmit} disabled={busy || !form.amount} onClick={submit} className="px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50">
                {busy ? t("wallet.converting") : t("wallet.convert")}
              </button>
            </>
          )}
        </div>

        {/* Balances side panel */}
        <div className="border border-[#2A2E38] bg-[#141720]">
          <div className="px-6 py-4 border-b border-[#2A2E38]">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">{t("wallet.live_balances")}</div>
            <h3 className="font-display text-lg font-bold mt-1">{t("wallet.your_holdings")}</h3>
          </div>
          <div className="divide-y divide-[#2A2E38] max-h-[520px] overflow-y-auto">
            {visibleBalances.map((b) => (
              <div key={b.currency} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{b.currency}</div>
                  <div className="text-[10px] tracking-wider uppercase text-[#6E7587]">{b.kind}</div>
                </div>
                <div className="text-right font-mono text-sm">
                  {b.kind === "fiat" ? formatFiat(b.balance, b.currency) : formatCrypto(b.balance)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
